import { useEffect, useRef } from 'react'

import type { TabKey } from './nav'

// ============================================================================
// RE-TAPPING THE ACTIVE TAB — board 27: "Re-tapping the tab you're on doesn't replay it — it
// scrolls that page to the top."
//
// The shell already scrolls every scrolled region of the visible page back to the top
// (mobile/shell/NavStage.tsx), which covers every screen as it is today. This event is for a
// screen that wants to do more on a re-tap (reset a filter, focus a field): subscribe with
// `useTabReselect`. A plain module-level emitter rather than a context, because the bar and the
// screens are siblings and there is no state to share — only a moment.
// ============================================================================

type Listener = (tab: TabKey) => void

const listeners = new Set<Listener>()

export function emitTabReselect(tab: TabKey): void {
  for (const listener of [...listeners]) listener(tab)
}

export function subscribeTabReselect(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Run `onReselect` when the owner re-taps `tab` while it is already the active one. */
export function useTabReselect(tab: TabKey, onReselect: () => void): void {
  const latest = useRef(onReselect)
  latest.current = onReselect
  useEffect(
    () =>
      subscribeTabReselect((t) => {
        if (t === tab) latest.current()
      }),
    [tab],
  )
}
